import './PlanMapper.scss';
import * as M from "../Modal";
import * as React from "react";
import * as H from "../../hooks";
import * as C from "../../Common";
import * as S from "../../services";
import * as BS from "react-bootstrap";
import { darken } from "@mui/material";
import * as PM from "../../PurposeModal";
import { Dialog } from "@material-ui/core";
import { TC, T, FP, LT } from "../../Constants";
import { Document, Page, pdfjs } from 'react-pdf';
import * as US from "../../services/user.service";
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { SavedData, UploaderProps } from "./PlanUploader";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

//#region Types
type AnnotationProps = {
    /** Callback to close the modal */
    close?: () => void;
    /** Callback to save the plans */
    onSave?: (plans: T.Plan[]) => void;
    /** The JSON data you need to use */
    data: Parameters<UploaderProps["onValidate"]>[0];
    /** The default plans */
    plans?: T.Plan[];
    set_data: (data: any) => void;
}

type Annotation = T.Plan["annotations"][number]

type Equipment = {
    id: string;
    name: string;
    location: {
        site: string;
        building: string;
        emplacement?: string;
        local?: string;
    };
}

type contextEquipments = {
    id: string;
    name: string;
    location: {
        site: string;
        building: string;
        emplacement?: string;
        local?: string;
    };
}
//#endregion

//#region Constants
// Add this color mapping constant at the top of the file with other constants
const LINK_TYPE_COLORS: Record<string, string> = {
    LT_ELEC: '#FFD700',  // yellow for electricity
    LT_GAS: '#00FF00',   // green for gas
    LT_WATER: '#0000FF', // blue for water
    LT_THCH: '#FF0000',   // red for hot
    LT_THFR: '#00FFFF',  // light blue for cold
};

// Constante pour la qualité de base du rendu (indépendante du zoom)
const BASE_RENDER_QUALITY = 3.0;
// 0.1 seconds in milliseconds
const CLICK_DURATION_THRESHOLD = 100;
//#endregion

const Annotations: React.FC<AnnotationProps> = ({ close, onSave, set_data, plans, ...props }) => {
    // The tool used to translate text
    const lg = H.useLanguage();
    // The annotations created on the images
    const [annotations, set_annotations] = React.useState<Annotation[]>([]);
    // The container for the annotations component
    const annotContainer = React.useRef<HTMLDivElement | null>(null);
    // Selected equipments for the link type dialog
    const [selectedEquipments, setSelectedEquipments] = React.useState<contextEquipments | null>(null);
    // The equipments names
    const [equipments, setEquipments] = React.useState<string[]>([]);
    // The equipments retrieved from the context
    const [contextEquipments, setContextEquipments, status] = H.useAsyncState<ReturnType<T.API.EDL.PLAN.GetEquipmentsAnnotations>>([]);
    // Selected annotations
    const [selectedItems, setSelectedItems] = React.useState<number[]>([]);
    // Add these new state variables with the existing ones
    const [selectionBox, setSelectionBox] = React.useState<Record<"startX" | "startY" | "endX" | "endY", number>>(null);

    const pdfContainer = React.useRef(null);
    const [links, set_links] = React.useState(props.data?.links || []);
    // Add zoom controls
    const [scale, setScale] = React.useState(1);
    const [isDragging, setIsDragging] = React.useState(false);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });


    const [isRightDragging, setIsRightDragging] = React.useState(false);
    const [dialogPosition, setDialogPosition] = React.useState<{ x: number, y: number } | null>(null);
    const [clickStartTime, setClickStartTime] = React.useState<number | null>(null);

    // Selected links
    const [selectedLinks, setSelectedLinks] = React.useState<number[]>([]);

    // Add this state for the link type dialog
    const [linkTypeDialog, setLinkTypeDialog] = React.useState<{
        show: boolean;
        sourceAnnotation: number;
        targetAnnotation: number;
        sourceEquipment: string | null;
        targetEquipment: string | null;
    } | null>(null);

    // Add this new state near other state declarations
    const [toolbarWidth, setToolbarWidth] = React.useState(400); // Default width
    const [isResizing, setIsResizing] = React.useState(false);

    // The equipments ids set from this code
    const [idArray, setIdArray] = React.useState<number[]>([]);
    // The equipments AISET ids
    const [EquipmentIdArray, setEquipmentIdArray] = React.useState<string[]>([]);

    // Data saved from a previous work
    const [savedData, setSavedData] = React.useState<SavedData | null>(props.data?.savedData || null);

    const [existingLinks, setExistingLinks] = H.useAsyncState<ReturnType<T.API.EDL.PLAN.GetExistingLinks>>([]);

    // Check if the plan already exists in the database
    const [existInDb, setExistInDb] = React.useState(false);

    const [planName, setPlanName] = React.useState(props.data?.savedData?.name || "");

    const [dialogNewEquipment, setDialogNewEquipment] = React.useState<{ x: number, y: number, annotation: Annotation } | null>(null);

    const root_type = React.useMemo<string>(() => props.data.submission.type, [props.data.submission.type]);
    const roots = React.useMemo<T.ContextParams>(() => ({ roots: props.data.submission.roots }), [props.data.submission.roots]);

    React.useEffect(() => {
        let is_subscribed = true;
        S.getEquipmentsAnnotations({ context: roots, type: root_type })
            .then(equipments => is_subscribed && setContextEquipments(equipments.data, "done"))
            .catch(() => is_subscribed && setContextEquipments([], "error"));
        return () => {
            is_subscribed = false;
            setContextEquipments([], "load");
        }
    }, [roots]);

    React.useEffect(() => {
        let isSubscribed = true;
        if (EquipmentIdArray.length > 1) S.getExistingLinks({ equipmentIds: EquipmentIdArray, context: roots })
            .then(existingLinks => isSubscribed && setExistingLinks(existingLinks.data, "done"))
            .catch(() => isSubscribed && setExistingLinks([], "error"));
        else setExistingLinks([], "done");
        return () => {
            isSubscribed = false;
            setExistingLinks([], "load");
        }
    }, [EquipmentIdArray, setExistingLinks]);

    React.useEffect(() => {
        // find the new links in the existingLinks and add them to the links
        if (existingLinks) {
            const newLinks = existingLinks.filter(link => !links.some(l => l.id === link.id));
            const newLinksFormatted = newLinks.map(link => ({
                id: link.id,
                type: link.type,
                sourceEquipment: link.source,
                targetEquipment: link.target,
                targetAnnotation: annotations.find(a => a.data.equipment_id === link.target)?.data.id,
                sourceAnnotation: annotations.find(a => a.data.equipment_id === link.source)?.data.id,
            }));
            set_links(prev => [...prev, ...newLinksFormatted]);
        }

    }, [existingLinks]);

    React.useEffect(() => {
        annotContainer.current?.focus();
    }, []);

    // Initialize annotations with PDF data on component mount
    React.useEffect(() => {
        if (savedData) {
            set_annotations(savedData.annotations);
            setEquipments(savedData.annotations.map(a => a.data.text));
            setEquipmentIdArray(savedData.annotations.filter(a => a.data.equipment_id).map(a => a.data.equipment_id));
            setIdArray(savedData.annotations.map(a => a.data.id));
        }
        else if (props.data?.nodes?.length) {
            const pdfAnnotations: Annotation[] = props.data.nodes.map((data, index) => ({
                geometry: {
                    type: 'RECTANGLE',
                    x: data.bounding_box.x,
                    y: data.bounding_box.y,
                    width: data.bounding_box.width,
                    height: data.bounding_box.height,
                },
                data: {
                    open: false,
                    id: index + 1,
                    hidden: false,
                    type: data.type,
                    equipment_id: data.aiset_id,
                    text: data.name || `PDF Object ${index + 1}`,
                    isPdfData: true, // Flag to identify PDF annotations
                }
            }));
            setEquipments(pdfAnnotations.map(a => a.data.text));
            setEquipmentIdArray(pdfAnnotations.filter(a => a.data.equipment_id).map(a => a.data.equipment_id));
            set_annotations(pdfAnnotations);
            setIdArray(pdfAnnotations.map(a => a.data.id));
        };
    }, [props.data]);

    const renderDataOnPDF = React.useCallback(() => <div className="pdf-overlay">
        <svg
            style={{
                top: 0,
                left: 0,
                zIndex: 1,
                width: '100%',
                height: '100%',
                position: 'absolute',
                pointerEvents: 'none',
                textRendering: 'geometricPrecision',
                shapeRendering: 'geometricPrecision',
            }}
        >
            {links.map((link, index) => {
                // Find the corresponding annotations for source and target
                const sourceAnnotation = annotations.find(a => a.data.id === link.sourceAnnotation);
                const targetAnnotation = annotations.find(a => a.data.id === link.targetAnnotation);

                // Skip rendering if either box is hidden
                if (!sourceAnnotation || !targetAnnotation ||
                    sourceAnnotation.data.hidden || targetAnnotation.data.hidden) {
                    return null;
                }

                if (!sourceAnnotation || !targetAnnotation) return null;

                const sourceX = sourceAnnotation.geometry.x + (sourceAnnotation.geometry.width / 2);
                const sourceY = sourceAnnotation.geometry.y + (sourceAnnotation.geometry.height / 2);
                const targetX = targetAnnotation.geometry.x + (targetAnnotation.geometry.width / 2);
                const targetY = targetAnnotation.geometry.y + (targetAnnotation.geometry.height / 2);

                const isSelected = selectedLinks.includes(index);
                const linkColor = LINK_TYPE_COLORS[link.type] || '#0000FF';

                return (
                    <g key={`link-${index}`}>
                        {/* Shadow effect for selected links */}
                        {isSelected && (
                            <line
                                x1={sourceX}
                                y1={sourceY}
                                x2={targetX}
                                y2={targetY}
                                stroke="yellow"
                                strokeWidth="12"
                                strokeOpacity="0.7"
                                filter="url(#glow)"
                                style={{ pointerEvents: 'none' }}
                            />
                        )}
                        {/* Invisible wider line for better click detection */}
                        <line
                            x1={sourceX}
                            y1={sourceY}
                            x2={targetX}
                            y2={targetY}
                            stroke="transparent"
                            strokeWidth="20"
                            style={{ pointerEvents: 'all', cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItems([]);
                                if (e.ctrlKey) setSelectedLinks(prev =>
                                    prev.includes(index)
                                        ? prev.filter(i => i !== index)
                                        : [...prev, index]
                                )
                                else setSelectedLinks([index]);
                            }}
                        />
                        {/* Visible line */}
                        <line
                            x1={sourceX}
                            y1={sourceY}
                            x2={targetX}
                            y2={targetY}
                            stroke={linkColor}
                            strokeWidth={2 / scale}
                            markerEnd={`url(#arrowhead-${link.type})`}
                            style={{ pointerEvents: 'none' }}
                        />
                    </g>
                );
            })}
            <defs>
                {Object.entries(LINK_TYPE_COLORS).map(([type, color]) => (
                    <marker
                        refX="9"
                        key={type}
                        refY="3.5"
                        orient="auto"
                        markerWidth="10"
                        markerHeight="7"
                        id={`arrowhead-${type}`}
                    >
                        <polygon points="0 0, 10 3.5, 0 7" fill={color} />
                    </marker>
                ))}
            </defs>
        </svg>

        {/* Existing box rendering code */}
        {annotations
            .filter(annot => !annot.data.hidden)
            .map(annot => {
                const scaledStyle: React.CSSProperties = {
                    position: 'absolute',
                    top: annot.geometry.y,
                    left: annot.geometry.x,
                    width: annot.geometry.width,
                    height: annot.geometry.height,
                    border: '1px solid red', // before change : 2px solid red
                    boxShadow: selectedItems.includes(annot.data.id) ? '0 0 10px yellow' : 'none',
                    cursor: 'pointer',
                    backgroundColor: selectedItems.includes(annot.data.id)
                        ? 'rgba(255, 255, 0, 0.1)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)',
                };

                const textStyle: React.CSSProperties = {
                    position: 'absolute',
                    top: annot.geometry.y - 20 + (3 * scale),
                    left: annot.geometry.x,
                    backgroundColor: annot.data.equipment_id
                        ? 'transparent'
                        : 'rgba(255, 0, 0, 0.6)',
                    padding: '1px 1px',
                    fontSize: `${12 / scale}px`,
                    userSelect: 'text',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: selectedItems.includes(annot.data.id) ? 'bold' : 'normal',
                    boxShadow: selectedItems.includes(annot.data.id)
                        ? '0 2px 4px rgba(255, 255, 0, 0.5)'
                        : '0 2px 4px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s ease',
                    textRendering: 'geometricPrecision',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden'
                };

                return <div
                    key={annot.data.id}
                    onClick={(e) => handleItemClick(e, annot.data.id)}
                    onContextMenu={(e) => handleRightClickOnAnnotation(e, annot.data.id)}
                >
                    <div style={scaledStyle} />
                    <div id={`pdf-text-${annot.data.id}`} style={textStyle} children={annot.data.text} />
                </div>;
            })}
    </div>, [links, annotations, selectedLinks, selectedItems, scale]);


    const handleWheel = React.useCallback((e: WheelEvent | React.WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            const container = annotContainer.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();

            // Calculate mouse position relative to container
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            // Calculate mouse position relative to content
            const contentX = (mouseX - position.x) / scale;
            const contentY = (mouseY - position.y) / scale;

            const delta = e.deltaY;
            const zoomFactor = 0.3;
            const newScale = scale + (delta > 0 ? -zoomFactor : zoomFactor);
            const boundedScale = Math.min(Math.max(0.5, newScale), 4);

            // Calculate new position to keep mouse point fixed
            const newX = mouseX - contentX * boundedScale;
            const newY = mouseY - contentY * boundedScale;

            setPosition({ x: newX, y: newY });
            setScale(boundedScale);
        }
    }, [scale, position]);

    // Update the useEffect to capture the event earlier
    React.useEffect(() => {
        const container = annotContainer.current;
        if (container) {
            // Add the event listener with capture phase
            container.addEventListener('wheel', handleWheel, { passive: false, capture: true });
            return () => container.removeEventListener('wheel', handleWheel, { capture: true });
        }
    }, [scale, position, handleWheel]); // Add position as dependency

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button === 0) { // Clic gauche
            setClickStartTime(Date.now());
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
        else if (e.button === 2 && !e.shiftKey) { // Clic droit sans shift
            e.preventDefault();
            setIsRightDragging(true);
            const rect = annotContainer.current?.getBoundingClientRect();
            if (rect) {
                const x = (e.clientX - rect.left - position.x) / scale;
                const y = (e.clientY - rect.top - position.y) / scale;
                setSelectionBox({ startX: x, startY: y, endX: x, endY: y });
            }
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
        else if (isRightDragging && selectionBox) {
            const rect = annotContainer.current?.getBoundingClientRect();
            if (rect) {
                const x = (e.clientX - rect.left - position.x) / scale;
                const y = (e.clientY - rect.top - position.y) / scale;
                setSelectionBox(prev => ({ ...prev!, endX: x, endY: y }));
            }
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (e.button === 0) setIsDragging(false);
        else if (e.button === 2 && !e.shiftKey) { // Clic droit sans shift
            setIsRightDragging(false);
            if (selectionBox) setDialogPosition({ x: e.clientX, y: e.clientY });
        }
    };

    const handleRightClickOnAnnotation = (e: React.MouseEvent, id: number) => {
        if (e.button === 2) { // Clic droit
            e.preventDefault();
            e.stopPropagation();

            if (e.shiftKey) { // Si shift est pressé
                setIsRightDragging(false);
                setIsDragging(false);
                setSelectedItems([id]);

                const annotation = annotations.find(a => a.data.id === id);
                if (annotation) setDialogNewEquipment({ x: annotation.geometry.x, y: annotation.geometry.y, annotation: annotation });
                else setDialogNewEquipment(null);
            }
        }
    };

    const currentPlan = React.useMemo(() => plans?.[0], [plans]);

    // Fonction pour centrer la vue sur une annotation
    const centerOnAnnotation = React.useCallback((annotationId: number) => {
        const annotation = annotations.find(a => a.data.id === annotationId);
        if (!annotation || !annotContainer.current) return;

        const containerRect = annotContainer.current.getBoundingClientRect();

        // Calcule le centre de l'annotation
        const annotationCenterX = annotation.geometry.x + (annotation.geometry.width / 2);
        const annotationCenterY = annotation.geometry.y + (annotation.geometry.height / 2);

        // Calcule la nouvelle position pour centrer l'annotation
        const newX = (containerRect.width / 2) - (annotationCenterX * scale);
        const newY = (containerRect.height / 2) - (annotationCenterY * scale);

        // Applique la nouvelle position avec une animation
        setPosition({ x: newX, y: newY });
    }, [annotations, scale]);

    const centerOnLink = React.useCallback((linkIndex: number) => {
        const link = links[linkIndex];
        if (!link || !annotContainer.current) return;

        const sourceAnnotation = annotations.find(a => a.data.id === link.sourceAnnotation);
        const targetAnnotation = annotations.find(a => a.data.id === link.targetAnnotation);

        if (!sourceAnnotation || !targetAnnotation) return;

        const containerRect = annotContainer.current.getBoundingClientRect();

        // Calcule le centre du lien
        const linkCenterX = (sourceAnnotation.geometry.x + targetAnnotation.geometry.x) / 2;
        const linkCenterY = (sourceAnnotation.geometry.y + targetAnnotation.geometry.y) / 2;

        // Calcule la nouvelle position pour centrer le lien
        const newX = (containerRect.width / 2) - (linkCenterX * scale);
        const newY = (containerRect.height / 2) - (linkCenterY * scale);

        // Applique la nouvelle position
        setPosition({ x: newX, y: newY });
    }, [links, annotations, scale]);

    // Modifie la fonction change.toggle pour inclure le centrage
    const change = React.useMemo(() => ({
        toggle: (id: number) => setSelectedItems((prev) => {
            const isSelected = prev.includes(id);
            if (isSelected) return prev.filter((item) => item !== id);
            else {
                centerOnAnnotation(id); // Centre la vue sur l'annotation sélectionnée
                return [...prev, id];
            }
        }),
        delete: (id: number) => {
            // Demande de confirmation
            M.askConfirm({ title: TC.DELETE, text: TC.GLOBAL_CONFIRM_DELETE }).then(
                (confirmed) => {
                    if (confirmed) {
                        // Récupère le texte de l'annotation supprimée
                        const deletedAnnotation = annotations.find(a => a.data.id === id);
                        const deletedText = deletedAnnotation?.data?.text;

                        setEquipmentIdArray(prev => prev.filter(e => e !== deletedAnnotation?.data?.equipment_id));
                        setIdArray(prev => prev.filter(e => e !== deletedAnnotation?.data?.id));
                        setEquipments(prev => prev.filter(e => e !== deletedText));

                        // Supprime l'annotation
                        set_annotations((prev) => {
                            const updatedAnnotations = prev.filter(a => a.data.id !== id);
                            // Supprime les liens associés si c'est une annotation PDF
                            if (deletedText && links) {
                                const linksToKeep = links.filter(link => link.sourceAnnotation !== id && link.targetAnnotation !== id);
                                if (links.length !== linksToKeep.length) set_links(linksToKeep);
                            }

                            return updatedAnnotations;
                        });
                    }
                }
            );
        },
        edit: (id: string) => {
            if (id) M.renderFormModal<T.EquipmentData>({ path: FP.EQUIPEMENT_FORM, modalProps: { size: "lg" }, submissionId: id }).then(new_equip => {
                if (new_equip) {
                    if (new_equip.data.name) {
                        set_annotations(prev => prev.map(a => a.data.equipment_id === id ? { ...a, data: { ...a.data, text: new_equip.data.name } } : a));
                        setEquipments(prev => prev.map(e => e === new_equip.data.name ? new_equip.data.name : e));
                    }
                }
            }).catch(M.Alerts.updateError);
            else M.Alerts.updateError("Cannot edit an equipment without an id");
        }
    }), [annotations, plans, centerOnAnnotation, links]);

    const change_plan = React.useMemo(() => ({
        rename: (id: string, name?: string) => M.askPrompt({ isRequired: true, defaultVal: name, label: TC.GLOBAL_NAME })
            .then(val => {
                if (typeof val === "string") US.bulkPlans([{ updateOne: { filter: { _id: savedData._id }, update: { name: val } } }]).then(res => {
                    setPlanName(val);
                    setSavedData(res.data);
                    M.Alerts.success_alert();
                })
                    .catch(e => {
                        console.error('Error renaming plan:', e);
                        M.Alerts.updateError(e);
                    });
            })
    }), [savedData]);

    const annot_recap = React.useMemo(() => {
        const active = annotations.filter(a => !a.data.hidden);
        const active_ids = active.map(a => a.data.id);
        const opened_ids = annotations.filter(a => a.data.open).map(a => a.data.id);
        return { not_hidden: active, active: active_ids, open: opened_ids };
    }, [annotations]);

    const close_modal = React.useCallback(() => close?.(), [close]);

    const savePlanAnnotations = React.useCallback(() => {
        let unmount = M.renderLoader();
        if (savedData?._id || existInDb) US.bulkPlans([{ updateOne: { filter: { _id: savedData._id }, update: { annotations: annotations, name: planName } } }])
            .then(res => M.Alerts.success_alert())
            .catch(e => M.Alerts.updateError(e))
            .finally(unmount);
        else if (!existInDb) M.askPrompt({ isRequired: true, title: TC.SAVE, label: TC.GLOBAL_NAME_PLAN, defaultVal: null, }).then(planName => {
            let unmount = M.renderLoader();
            if (typeof planName === "string") {
                const plan: Omit<T.Plan, "_id"> = {
                    name: planName,
                    type: "pdf_entity",
                    src: props.data.pdf,
                    annotations: annotations,
                    origin: props.data.submission.roots,
                    location: props.data.submission.roots,
                }
                US.insertPlan(plan).then(res => {
                    setExistInDb(true);
                    setPlanName(planName);
                    setSavedData(res.data);
                    M.Alerts.success_alert();
                })
                    .catch(M.Alerts.updateError)
                    .finally(unmount);
            }
        });
    }, [props.data.submission, annotations, props.data.pdf, planName]);

    const removePlanAnnotations = React.useCallback(() => {
        M.askConfirm({ title: TC.DELETE, text: TC.GLOBAL_CONFIRM_DELETE }).then(confirmed => {
            if (confirmed) US.bulkPlans([{ deleteOne: { filter: { _id: savedData._id } } }]).then(res => {
                M.Alerts.success_alert();
                close?.();
            })
                .catch(M.Alerts.updateError);
        });
    }, [savedData]);

    // Add this handler for the Page component
    const handlePageClick = (e: React.MouseEvent) => {
        const clickDuration = clickStartTime ? Date.now() - clickStartTime : 0;

        // Only deselect if it's a quick click (less than threshold)
        if (clickDuration < CLICK_DURATION_THRESHOLD) {
            const target = e.target as HTMLElement;
            if (target.classList.contains('react-pdf__Page') ||
                target.classList.contains('react-pdf__Page__canvas')) {
                setSelectedItems([]);
                setSelectedLinks([]); // Clear selected links as well
            }
        }
        setClickStartTime(null);
    };

    // Add new function to handle annotation submission
    const handleAnnotationSubmit = (id: number = null) => {
        PM.renderContextElements({ context: roots, isRequired: true, add_parent: true, add_all_parents: true }).then(parent => {
            // User did not cancel parent pick
            // then display a modal to choose create or select equipment
            const new_id = idArray.length > 0 ? Math.max(...idArray) + 1 : 1;

            if (typeof parent === "string") {
                // Create an equipment
                M.renderFormModal<T.EquipmentData>({ path: FP.EQUIPEMENT_FORM, modalProps: { size: "lg" }, }).then(new_equip => {

                    if (new_equip) {
                        const unmount = M.renderLoader();
                        S.attachNode({ parent: parent, children: new_equip._id, type: LT.LINK_TYPE_OWN }).then(() => {

                            if (!id) {
                                const newAnnotation: Annotation = {
                                    geometry: {
                                        type: 'RECTANGLE',
                                        x: Math.min(selectionBox?.startX ?? 0, selectionBox?.endX ?? 0),
                                        y: Math.min(selectionBox?.startY ?? 0, selectionBox?.endY ?? 0),
                                        width: Math.abs((selectionBox?.endX ?? 0) - (selectionBox?.startX ?? 0)),
                                        height: Math.abs((selectionBox?.endY ?? 0) - (selectionBox?.startY ?? 0)),
                                    },
                                    data: {
                                        id: new_id,
                                        equipment_id: new_equip._id,
                                        text: new_equip.data.name,
                                        hidden: false,
                                        open: false,
                                        isPdfData: false,
                                        type: 'test'
                                    }
                                };

                                // Mettre à jour les annotations 
                                set_annotations(prev => [...prev, newAnnotation]);

                                setEquipments(prev => [...prev, ...(newAnnotation.data.text ? [newAnnotation.data.text] : [])]);
                                setIdArray(prev => [...prev, new_id]);
                                if (new_equip._id) setEquipmentIdArray(prev => [...prev, new_equip._id]);
                            }
                            else {
                                // Update the existing annotation
                                const old_annotation = annotations.find(a => a.data.id === id);
                                if (old_annotation) {
                                    set_annotations(prev => prev.map(a => a.data.id === id ? { ...a, data: { ...a.data, equipment_id: new_equip._id, text: new_equip.data.name } } : a));
                                    setEquipments(prev => prev.map(e => e === old_annotation.data.text ? new_equip.data.name : e));
                                    if (new_equip._id) {
                                        // d'abord on retire l'id de l'ancien equipment
                                        setEquipmentIdArray(prev => prev.filter(e => e !== old_annotation.data.equipment_id));
                                        // puis on ajoute le nouvel equipment
                                        setEquipmentIdArray(prev => [...prev, new_equip._id]);
                                    }
                                }
                            }
                        })
                            .catch(M.Alerts.updateError)
                            .finally(unmount);
                    }
                }).catch(M.Alerts.updateError);
            }
        }).catch(M.Alerts.loadError);

    };

    const handleAnnotationSubmitExistingEquipment = (equipment: contextEquipments) => {

        const new_id = idArray.length > 0 ? Math.max(...idArray) + 1 : 1;

        const newAnnotation: Annotation = {
            geometry: {
                type: 'RECTANGLE',
                x: Math.min(selectionBox?.startX ?? 0, selectionBox?.endX ?? 0),
                y: Math.min(selectionBox?.startY ?? 0, selectionBox?.endY ?? 0),
                width: Math.abs((selectionBox?.endX ?? 0) - (selectionBox?.startX ?? 0)),
                height: Math.abs((selectionBox?.endY ?? 0) - (selectionBox?.startY ?? 0)),
            },
            data: {
                id: new_id,
                equipment_id: equipment.id,
                text: equipment.name,
                hidden: false,
                open: false,
                isPdfData: false,
                type: 'test'
            }
        };

        set_annotations(prev => [...prev, newAnnotation]);
        setIdArray(prev => [...prev, new_id]);
        if (newAnnotation.data.text) {
            setEquipmentIdArray(prev => [...prev, equipment.id]);
        }
        // Retirer l'ancien equipment dans equipments
        setEquipments(prev => prev.filter(e => e !== equipment.name));
        // Ajouter le nouvel equipment dans equipments
        setEquipments(prev => [...prev, ...(newAnnotation.data.text ? [newAnnotation.data.text] : [])]);
    }


    // Modify the handleCreateLink function
    const handleCreateLink = (type: string) => {
        if (linkTypeDialog && linkTypeDialog.sourceEquipment !== linkTypeDialog.targetEquipment) {
            var newLink = {
                sourceAnnotation: linkTypeDialog.sourceAnnotation,
                targetAnnotation: linkTypeDialog.targetAnnotation,
                sourceEquipment: linkTypeDialog.sourceEquipment,
                targetEquipment: linkTypeDialog.targetEquipment,
                type: type,
                id: null
            };

            S.attachNode({ parent: linkTypeDialog.sourceEquipment, children: linkTypeDialog.targetEquipment, type: type })
                .then((response) => {
                    newLink.id = response.data[0]._id; // Access first element of response.data array
                    const updatedLinks = [...links, newLink];
                    set_links(updatedLinks);
                })
                .catch(M.Alerts.updateError);
            // Clear selections and dialog
            setSelectedItems([]);
            setLinkTypeDialog(null);
        }
        else if ((linkTypeDialog.sourceEquipment === undefined || linkTypeDialog.sourceEquipment === null) || (linkTypeDialog.targetEquipment === undefined || linkTypeDialog.targetEquipment === null)) {
            M.Alerts.updateError("Cannot create a link between two invalid equipments");
            setSelectedItems([]);
            setLinkTypeDialog(null);
        }
        else {
            M.Alerts.updateError("Cannot create a link between the same equipment.");
            setSelectedItems([]);
            setLinkTypeDialog(null);
        }
    };

    // Add keyboard event handler
    const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
        if (e.key === 'Delete' && selectedItems.length > 0) {
            e.preventDefault();

            M.askConfirm({
                title: TC.DELETE,
                text: selectedItems.length === 1
                    ? TC.GLOBAL_CONFIRM_DELETE
                    : `${TC.GLOBAL_CONFIRM_DELETE} (${selectedItems.length} items)`
            }).then(confirmed => {
                if (confirmed) {
                    set_annotations(prev => {
                        // Get all texts of annotations being deleted
                        const deletedIds = prev
                            .filter(a => selectedItems.includes(a.data.id))
                            .map(a => a.data.id);

                        // Filter out the deleted annotations
                        const updatedAnnotations = prev.filter(a => !selectedItems.includes(a.data.id));

                        // Update links
                        set_links(prevLinks => prevLinks.filter(link =>
                            !deletedIds.includes(link.sourceAnnotation) && !deletedIds.includes(link.targetAnnotation)
                        ));

                        return updatedAnnotations;
                    });

                    // Clear selection
                    setSelectedItems([]);
                }
            });
        }
    }, [selectedItems]);

    // Add effect for keyboard listener
    React.useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Add this function with the other handlers
    const handleItemClick = React.useCallback((e: React.MouseEvent, id: number) => {
        e.stopPropagation();

        if (e.ctrlKey || e.metaKey) {
            setSelectedItems(prev =>
                prev.includes(id)
                    ? prev.filter(item => item !== id)
                    : [...prev, id]
            );
        } else {
            setSelectedItems([id]);
        }

        setSelectedLinks([]);

        const updatedSelection = e.ctrlKey || e.metaKey
            ? (selectedItems.includes(id)
                ? selectedItems.filter(item => item !== id)
                : [...selectedItems, id])
            : [id];

        if (updatedSelection.length === 2) {
            const [sourceId, targetId] = updatedSelection;
            const sourceAnnotation = annotations.find(a => a.data.id === sourceId);
            const targetAnnotation = annotations.find(a => a.data.id === targetId);

            if (sourceAnnotation && targetAnnotation) {
                // Utilisez les id pour créer le lien
                setLinkTypeDialog({
                    show: true,
                    sourceAnnotation: sourceAnnotation.data.id,
                    targetAnnotation: targetAnnotation.data.id,
                    sourceEquipment: sourceAnnotation.data.equipment_id,
                    targetEquipment: targetAnnotation.data.equipment_id,
                });
            }
        }
    }, [selectedItems, annotations]);

    // Add these effects after other useEffect declarations
    React.useEffect(() => {
        if (isResizing) {
            const handleMouseMove = (e: MouseEvent) => {
                const newWidth = window.innerWidth - e.clientX;
                setToolbarWidth(Math.max(100, newWidth));
            };

            const handleMouseUp = () => {
                setIsResizing(false);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing]);

    const getEquipmentLocationAndName = (equipment: Equipment) => {
        const parts = [
            equipment?.location?.site,
            equipment?.location?.building,
            equipment?.location?.emplacement,
            equipment?.location?.local,
            equipment?.name
        ].filter((part, index) => {
            // Only filter emplacement and local (index 2 and 3)
            return part || (index !== 2 && index !== 3);
        });
        return parts.join(' ➜ ');
    }

    return <Dialog open={true} fullScreen={true}>
        <C.Spinner status={status} >
            <div style={{ display: "grid", gridTemplateRows: "45px auto", height: "100%" }}>

                <div className="full_header">
                    <div className="nav_header">
                        <ul className="nav nav-tabs nav-justified">
                            <li className="nav-item">
                                <span className="nav-link active">{plans[0].name}</span>
                            </li>
                        </ul>
                    </div>
                    <button onClick={close_modal} className="quitButton"><i className="fa fa-times"></i></button>
                </div>

                <div className="annot_body position-relative">
                    <div className="annotationWrapper">
                        <div
                            ref={annotContainer}
                            className="annotationEditor"
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{
                                position: 'relative',
                                width: `calc(100% - ${toolbarWidth}px)`,
                                height: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                className="annotation-content"
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                    transformOrigin: '0 0',
                                    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                                    backfaceVisibility: 'hidden',
                                    // imageRendering: 'high-quality'
                                }}
                            >
                                <Document
                                    ref={pdfContainer}
                                    file={plans[0]?.src}
                                    onLoadError={(error) => console.error("PDF Load Error:", error)}
                                >
                                    <Page
                                        pageNumber={1}
                                        onClick={handlePageClick}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                        renderMode="canvas"
                                        devicePixelRatio={BASE_RENDER_QUALITY}
                                    >
                                        {renderDataOnPDF()}
                                    </Page>
                                </Document>
                                {selectionBox && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: Math.min(selectionBox.startX, selectionBox.endX),
                                            top: Math.min(selectionBox.startY, selectionBox.endY),
                                            width: Math.abs(selectionBox.endX - selectionBox.startX),
                                            height: Math.abs(selectionBox.endY - selectionBox.startY),
                                            border: '2px solid rgba(0, 123, 255, 0.8)',
                                            backgroundColor: 'rgba(0, 123, 255, 0.2)',
                                            pointerEvents: 'none',
                                            zIndex: 1000,
                                            boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.5)',
                                            backdropFilter: 'brightness(1.1)'
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <div
                            className="toolBar"
                            style={{ width: `${toolbarWidth}px` }}
                        >
                            {/* Add resize handle */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: -5,
                                    top: 0,
                                    bottom: 0,
                                    width: 10,
                                    cursor: 'ew-resize',
                                    zIndex: 10
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsResizing(true);
                                }}
                            />
                            <div className="main_tools">

                                <BS.ButtonGroup style={{ width: "100%" }}>
                                    <C.Button onClick={() => change_plan.rename(currentPlan?._id, planName)} children={TC.GLOBAL_RENAME} />
                                </BS.ButtonGroup>

                                {/* List of existing annotations */}

                                <div className="annotationList">
                                    <label>{lg.getStaticText(TC.EQUIPMENTS)}</label>
                                    {annotations.length === 0 ? (
                                        <C.CenterMessage />
                                    ) : (
                                        annotations.map((a) => (
                                            <div className="annotationItem" key={a.data.id} style={{ display: 'flex', alignItems: 'center' }}>
                                                <C.Button
                                                    className="button-annotation"
                                                    variant={selectedItems.includes(a.data.id) && a.data.equipment_id ? "primary" : selectedItems.includes(a.data.id) && !a.data.equipment_id ? "danger" : a.data.equipment_id ? "outline-primary" : "outline-danger"}
                                                    children={a.data.text}
                                                    onClick={() => {
                                                        change.toggle(a.data.id);

                                                        // Si deux éléments sont sélectionnés, afficher le dialogue
                                                        const newSelectedItems = selectedItems.includes(a.data.id)
                                                            ? selectedItems.filter((id) => id !== a.data.id)
                                                            : [...selectedItems, a.data.id];

                                                        if (newSelectedItems.length === 2) {
                                                            setLinkTypeDialog({
                                                                show: true,
                                                                sourceAnnotation: newSelectedItems[0],
                                                                targetAnnotation: newSelectedItems[1],
                                                                sourceEquipment: annotations.find(a => a.data.id === newSelectedItems[0])?.data.equipment_id,
                                                                targetEquipment: annotations.find(a => a.data.id === newSelectedItems[1])?.data.equipment_id,
                                                            });
                                                        }
                                                    }}
                                                />
                                                <C.Button
                                                    variant="outline-primary"
                                                    style={{ height: 'auto', alignSelf: 'stretch' }}
                                                    icon="pencil"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Empêche la propagation de l'événement
                                                        change.edit(a.data.equipment_id);
                                                    }}
                                                />
                                                <C.Button
                                                    variant="outline-danger"
                                                    style={{ height: 'auto', alignSelf: 'stretch' }}
                                                    icon="trash"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Empêche la propagation de l'événement
                                                        change.delete(a.data.id);
                                                    }}
                                                />
                                            </div>
                                        ))
                                    )}
                                </div>



                                {/* <div className="annotationList">
                                <label>{lg.getStaticText(TC.ANNOTATION_METERS)}</label>
                                {annotations.length === 0
                                    ? <C.CenterMessage />
                                    : annotations.map((a, i) => <div className="annotationItem" key={a.data.id}>
                                        <div className="annot_overview input-group">
                                            <div className="input-group-text">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedItems.includes(a.data.id)}
                                                    onChange={(e) => {
                                                        change.toggle(a.data.id, e);
                                                        // If we now have exactly 2 items selected, show link dialog
                                                        const newSelectedItems = selectedItems.includes(a.data.id) 
                                                            ? selectedItems.filter(id => id !== a.data.id)
                                                            : [...selectedItems, a.data.id];
                                                            
                                                        if (newSelectedItems.length === 2) {
                                                            setLinkTypeDialog({
                                                                show: true,
                                                                source: newSelectedItems[0],
                                                                target: newSelectedItems[1]
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <span className="input-group-text nameInput">{a.data.text}</span>
                                            <C.Button variant="outline-danger" icon="trash" onClick={() => change.delete(a.data.id)} />
                                        </div>
                                    </div>)}
                            </div> */}

                                {/* List of existing links */}

                                <div className="annotationListLinks">
                                    <label>{lg.getStaticText(TC.ANNOTATION_LINKS)}</label>
                                    {!links?.length
                                        ? <C.CenterMessage />
                                        : links.map((link, index) => (
                                            <div
                                                className="annotationItem"
                                                key={`link-${index}`}
                                                style={{ display: 'flex', alignItems: 'center' }} // Center align items
                                            >
                                                <div className="link-item" style={{ flex: 1 }}>
                                                    <span
                                                        className="input-group-text nameInput"
                                                        style={{
                                                            color: '#000000',
                                                            backgroundColor: selectedLinks.includes(index) ? darken(LINK_TYPE_COLORS[link.type], 0.2) : LINK_TYPE_COLORS[link.type],
                                                            transition: 'background-color 0.3s ease',
                                                            opacity: 0.6,
                                                            padding: '4px 12px',
                                                            borderRadius: '4px',
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedItems([]);
                                                            setSelectedLinks(prev =>
                                                                prev.includes(index)
                                                                    ? prev.filter(i => i !== index)
                                                                    : [index]
                                                            );
                                                            centerOnLink(index);
                                                        }}

                                                    >
                                                        <span>{annotations.find(a => a.data.id === link.sourceAnnotation)?.data.text || link.sourceAnnotation}</span>
                                                        <span style={{ margin: '0' }}>↓</span>
                                                        <span>{annotations.find(a => a.data.id === link.targetAnnotation)?.data.text || link.targetAnnotation}</span>
                                                        <span style={{ fontSize: '0.8em', marginTop: '1px' }}>({link.type})</span>
                                                    </span>
                                                </div>

                                                <C.Button
                                                    variant="outline-danger"
                                                    icon="trash"
                                                    style={{ height: 'auto', alignSelf: 'stretch' }} // Stretch button to match height
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        M.askConfirm({
                                                            title: TC.DELETE,
                                                            text: TC.GLOBAL_CONFIRM_DELETE
                                                        }).then(confirmed => {
                                                            if (confirmed && links) {
                                                                const newLinks = links.filter((_, i) => i !== index);
                                                                S.removeLinks(links[index].id).then(() => {
                                                                    set_links(newLinks);
                                                                    setSelectedLinks(prev => prev.filter(i => i !== index));
                                                                });
                                                            }
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <div>
                                <div className="close_menu">

                                    <C.Button
                                        icon="save"
                                        onClick={savePlanAnnotations}
                                        children={lg.getStaticText(TC.SAVE)}
                                    />

                                    <C.Button
                                        icon="trash"
                                        variant="outline-danger"
                                        disabled={!savedData?._id}
                                        onClick={removePlanAnnotations}
                                        children={lg.getStaticText(TC.DELETE)}
                                    />

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            {dialogPosition && (
                <div
                    style={{
                        position: 'fixed',
                        left: dialogPosition.x,
                        top: dialogPosition.y,
                        zIndex: 2000,
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid #ddd'
                    }}
                >
                    <div className="mb-3">
                        <p>{lg.getStaticText(TC.ANNOTATION_CONFIRM_CREATE_ANNOTATION)}</p>
                        <div>
                            <C.Form.Select
                                // required
                                typeahead={{ dropdownFit: true }}
                                no_clear_btn
                                labelPosition="left"
                                value={['test', 'test2']}
                                options={contextEquipments.map(equipment => ({ value: equipment?.id, label: getEquipmentLocationAndName(equipment) }))}
                                label={TC.EQUIPMENTS}
                                // error={{ code: 'error' }}
                                onChange={(equipment) => {
                                    const full_equipment = contextEquipments.find(e => e.id === equipment?.id);
                                    setSelectedEquipments(full_equipment || null);
                                }}
                                disabled={false}
                            />
                        </div>

                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <C.Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                                setDialogPosition(null);
                                setSelectionBox(null);
                            }}
                            children={lg.getStaticText(TC.GLOBAL_CANCEL)}
                        />
                        <C.Button
                            size="sm"
                            variant="primary"
                            disabled={!selectedEquipments}
                            onClick={() => {
                                setDialogPosition(null);
                                setSelectionBox(null);
                                handleAnnotationSubmitExistingEquipment(selectedEquipments || null);
                                setSelectedEquipments(null);
                            }}
                            // disabled={!annotationText.trim()}
                            // children={lg.getStaticText(TC.GLOBAL_CONFIRM)}
                            children={lg.getStaticText(TC.ANNOTATION_LINK_EQUIPMENT)}
                        />
                        <C.Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                                setDialogPosition(null);
                                setSelectionBox(null);
                                handleAnnotationSubmit();
                            }}
                            // disabled={!annotationText.trim()}
                            // children={lg.getStaticText(TC.GLOBAL_CONFIRM)}
                            children={lg.getStaticText(TC.ANNOTATION_CREATE_EQUIPMENT)}
                        />
                    </div>
                </div>
            )}
            {dialogNewEquipment && (
                <div
                    style={{
                        position: 'fixed',
                        left: dialogNewEquipment.x,
                        top: dialogNewEquipment.y,
                        zIndex: 2000,
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid #ddd'
                    }}
                >
                    <div className="mb-3">
                        <p>{lg.getStaticText(TC.ANNOTATION_NEW_EQUIPMENT_ON_ANNOTATION)}</p>
                        <div>
                            <C.Form.Select
                                // required
                                typeahead={{ dropdownFit: true }}
                                no_clear_btn
                                labelPosition="left"
                                value={['test', 'test2']}
                                options={contextEquipments.map(equipment => ({ value: equipment?.id, label: getEquipmentLocationAndName(equipment) }))}
                                label={TC.EQUIPMENTS}
                                // error={{ code: 'error' }}
                                onChange={(equipment) => {
                                    const full_equipment = contextEquipments.find(e => e.id === equipment?.id);
                                    setSelectedEquipments(full_equipment || null);
                                }}
                                disabled={false}
                            />
                        </div>

                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <C.Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                                setDialogNewEquipment(null);
                                setSelectionBox(null);
                            }}
                            children={lg.getStaticText(TC.GLOBAL_CANCEL)}
                        />
                        <C.Button
                            size="sm"
                            variant="primary"
                            disabled={!selectedEquipments}
                            onClick={() => {
                                setDialogNewEquipment(null);
                                setSelectionBox(null);
                                const newAnnotation = { ...dialogNewEquipment.annotation, data: { ...dialogNewEquipment.annotation.data, equipment_id: selectedEquipments?.id, text: selectedEquipments?.name } };
                                // Il faut modifier l'annotation dans le state
                                set_annotations(prev => prev.map(a => a.data.id === newAnnotation.data.id ? newAnnotation : a));
                                // puis on retire l'id de l'ancien equipment
                                setEquipmentIdArray(prev => prev.filter(e => e !== dialogNewEquipment.annotation.data.equipment_id));
                                // puis on ajoute le nouvel equipment
                                setEquipmentIdArray(prev => [...prev, selectedEquipments?.id]);
                                setSelectedEquipments(null);
                            }}
                            // disabled={!annotationText.trim()}
                            // children={lg.getStaticText(TC.GLOBAL_CONFIRM)}
                            children={lg.getStaticText(TC.ANNOTATION_LINK_EQUIPMENT)}
                        />
                        <C.Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                                setDialogNewEquipment(null);
                                setSelectionBox(null);
                                handleAnnotationSubmit(dialogNewEquipment.annotation.data.id);
                            }}
                            // disabled={!annotationText.trim()}
                            // children={lg.getStaticText(TC.GLOBAL_CONFIRM)}
                            children={lg.getStaticText(TC.ANNOTATION_CREATE_EQUIPMENT)}
                        />
                    </div>
                </div>
            )}
            {linkTypeDialog && (
                <Dialog
                    open={true}
                    onClose={() => setLinkTypeDialog(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <div style={{ padding: '20px' }}>
                        <h4>{lg.getStaticText(TC.ANNOTATION_CREATE_LINK)}</h4>
                        <p>
                            {annotations.find(a => a.data.id === linkTypeDialog.sourceAnnotation)?.data.text} → {annotations.find(a => a.data.id === linkTypeDialog.targetAnnotation)?.data.text}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {Object.keys(LINK_TYPE_COLORS).map(type => (
                                <C.Button
                                    key={type}
                                    onClick={() => handleCreateLink(type)}
                                    style={{
                                        backgroundColor: LINK_TYPE_COLORS[type],
                                        color: '#000000',
                                        opacity: 0.8
                                    }}
                                >
                                    {type.toUpperCase()}
                                </C.Button>
                            ))}
                            <C.Button
                                variant="secondary"
                                onClick={() => {
                                    setLinkTypeDialog(null);
                                    setSelectedItems([]);
                                }}
                            >
                                {lg.getStaticText(TC.GLOBAL_CANCEL)}
                            </C.Button>
                        </div>
                    </div>
                </Dialog>
            )}
        </ C.Spinner>
    </Dialog>
}

export default Annotations;